import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { personConstraintKeys } from "@/lib/query";
import type {
  AddPersonConstraintInput,
  UpdatePersonConstraintInput,
} from "@/schemas/person-constraints";

/**
 * One person's staffing constraints.
 *
 * `level` arrives NULL when the viewer may not see ratings — the server
 * redacts it, so the client is displaying an absence, not enforcing one.
 * `canViewRatings` exists to explain the blanks, never to decide them.
 */
export interface PersonConstraint {
  id: string;
  typeKey: string;
  /** The NOS guid, lowercased. */
  valueId: string;
  name: string;
  typeName: string;
  level: number | null;
  levelName: string | null;
  notes: string | null;
  /** Hidden by an Enable admin. The rating is untouched and still counts. */
  isHidden: boolean;
  /**
   * NOS no longer offers this value. Separate from `isHidden` on purpose: "we
   * stopped using this" and "the source dropped it" are different facts, and
   * only the second means nobody can act on it.
   */
  isMissingFromNos: boolean;
}

export interface PersonConstraintsView {
  constraints: PersonConstraint[];
  canViewRatings: boolean;
  canManage: boolean;
}

async function mutate(url: string, method: string, body?: unknown) {
  const res = await fetch(url, {
    method,
    ...(body !== undefined && {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => null);
    throw new Error(json?.error ?? "Request failed");
  }
  return res.status === 204 ? null : res.json().catch(() => null);
}

export function usePersonConstraints(personId: string, enabled = true) {
  return useQuery({
    queryKey: personConstraintKeys.list(personId),
    queryFn: async (): Promise<PersonConstraintsView> => {
      const res = await fetch(`/api/persons/${personId}/constraints`);
      if (!res.ok) throw new Error("Failed to fetch constraints");
      return (await res.json()).data;
    },
    enabled: enabled && Boolean(personId),
  });
}

export function useAddPersonConstraint(personId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddPersonConstraintInput) =>
      mutate(`/api/persons/${personId}/constraints`, "POST", input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personConstraintKeys.list(personId) });
      toast.success("Constraint added");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useUpdatePersonConstraint(personId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdatePersonConstraintInput & { id: string }) =>
      mutate(`/api/persons/${personId}/constraints/${id}`, "PATCH", input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personConstraintKeys.list(personId) });
      toast.success("Rating updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useRemovePersonConstraint(personId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mutate(`/api/persons/${personId}/constraints/${id}`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personConstraintKeys.list(personId) });
      toast.success("Constraint removed");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
